import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Container,
  CssBaseline,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
  Paper,
} from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import Navigation from "../../components/Navigation";
import AdminPageHeader from "../../components/AdminPageHeader";
import { DOC_SECTIONS } from "./docSections.jsx";
import { CHAPTER_COMPONENTS } from "./topics/index.js";

export default function AdminDocs() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const topic = searchParams.get("topic") || "organizations";

  const scrollToSection = (id) => {
    setSearchParams({ topic: id });
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  React.useEffect(() => {
    if (topic) {
      const el = document.getElementById(topic);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [topic]);

  const ChapterComponent = CHAPTER_COMPONENTS[topic];

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Documentation
                </Typography>
              </Breadcrumbs>
            </Box>

            <AdminPageHeader
              title="Admin Documentation"
              description="How things work in the admin panel"
              icon={<MenuBookIcon sx={{ color: "primary.main" }} />}
            />

            <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }}>
              <Paper
                variant="outlined"
                sx={{
                  width: { xs: "100%", md: 220 },
                  flexShrink: 0,
                  height: "fit-content",
                  position: { md: "sticky" },
                  top: { md: 100 },
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="600" gutterBottom>
                    Topics
                  </Typography>
                  <List dense disablePadding>
                    {DOC_SECTIONS.map((s) => (
                      <ListItemButton
                        key={s.id}
                        selected={topic === s.id}
                        onClick={() => {
                          scrollToSection(s.id);
                        }}
                        sx={{ borderRadius: 1 }}
                      >
                        <ListItemText primary={s.title} />
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              </Paper>

              <Card sx={{ flex: 1, minWidth: 0 }}>
                <CardContent>
                  {ChapterComponent ? (
                    <ChapterComponent onNavigate={navigate} />
                  ) : null}
                </CardContent>
              </Card>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
}
